import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import {
  PageHead, Card, Pill, StatusPill, Btn, Loading, Err, tableStyles, EmptyState, TextInput, Select,
} from "./_shared";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "saas",     label: "SaaS" },
  { key: "credits",  label: "Credits" },
  { key: "features", label: "Features" },
  { key: "limits",   label: "Limits" },
  { key: "usage",    label: "Usage" },
  { key: "audit",    label: "Audit" },
];

export default function AdminWorkspaceDetail() {
  const { workspaceId } = useParams();
  const [tab, setTab] = useState("overview");
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");
  const reload = useCallback(() =>
    api.get(`/api/admin/workspaces/${workspaceId}`).then(setDetail).catch((e) => setErr(e.message)),
  [workspaceId]);
  useEffect(() => { reload(); }, [reload]);

  if (err) return <Err msg={err} />;
  if (!detail) return <Loading />;
  const { workspace, owner, members = [], plan, balance } = detail;

  return (
    <div data-testid="admin-workspace-detail">
      <PageHead
        title={workspace.name}
        sub={`${workspace.workspace_type} · ${workspace.status} · ${workspace.id}`}
        right={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Pill tone="aqua">{balance?.available_balance ?? 0} credits</Pill>
            <Link to="/admin/workspaces" style={{ color: "var(--pl-text-muted)", fontSize: 12 }}>← All workspaces</Link>
          </div>
        }
      />

      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid var(--pl-border)", marginBottom: 18 }}>
        {TABS.map((t) => (
          <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} style={{
            padding: "10px 18px", border: 0, background: "transparent", cursor: "pointer",
            color: tab === t.key ? "var(--pl-aqua)" : "var(--pl-text-muted)",
            fontWeight: 800, fontSize: 11.5, letterSpacing: "0.16em", textTransform: "uppercase",
            borderBottom: "2px solid " + (tab === t.key ? "var(--pl-aqua)" : "transparent"),
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "overview" && <Overview workspace={workspace} owner={owner} plan={plan} members={members} balance={balance} />}
      {tab === "saas"     && <SaaS workspace={workspace} plan={plan} />}
      {tab === "credits"  && <Credits workspaceId={workspaceId} balance={balance} onChanged={reload} />}
      {tab === "features" && <Features workspaceId={workspaceId} />}
      {tab === "limits"   && <Limits workspaceId={workspaceId} />}
      {tab === "usage"    && <Usage workspaceId={workspaceId} />}
      {tab === "audit"    && <Audit workspaceId={workspaceId} />}
    </div>
  );
}

/* ───────────────── tabs ───────────────── */

function Overview({ workspace, owner, plan, members, balance }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <Card title="Workspace identity">
        <Row k="Name" v={workspace.name} />
        <Row k="ID" v={workspace.id} mono />
        <Row k="Type" v={workspace.workspace_type} />
        <Row k="Status" v={<StatusPill s={workspace.status} />} />
        <Row k="Parent" v={workspace.parent_workspace_id || "—"} />
        <Row k="Created" v={(workspace.created_at || "").slice(0, 19)} />
      </Card>
      <Card title="Owner">
        <Row k="Name"   v={owner?.name} />
        <Row k="Email"  v={owner?.email} />
        <Row k="Roles"  v={(owner?.roles || []).map((r) => <Pill key={r}>{r}</Pill>).reduce((acc, el, i) => acc.concat(i > 0 ? [" ", el] : [el]), [])} />
        <Row k="Plan"   v={plan ? plan.plan_id : "—"} />
      </Card>
      <Card title="Plan summary" style={{ gridColumn: "1 / -1" }}>
        {plan ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            <Mini k="Plan" v={plan.plan_id} />
            <Mini k="Cycle" v={plan.billing_cycle || "—"} />
            <Mini k="Status" v={<StatusPill s={plan.status || "active"} />} />
            <Mini k="Credits / mo" v={(plan.included_credits ?? "—").toString()} />
            <Mini k="Trial ends" v={(plan.trial_ends_at || "—").slice(0, 10)} />
            <Mini k="Renews" v={(plan.current_period_end || "—").slice(0, 10)} />
          </div>
        ) : <EmptyState title="No plan assigned" description="This workspace has no plan record yet." />}
      </Card>
      <Card title={`Members · ${members.length}`} style={{ gridColumn: "1 / -1" }}>
        {members.length === 0 ? (
          <EmptyState title="No members" description="Only the owner has access right now." />
        ) : (
          <table style={tableStyles.table}>
            <thead><tr>
              <th style={tableStyles.th}>User</th>
              <th style={tableStyles.th}>Role</th>
              <th style={tableStyles.th}>Joined</th>
            </tr></thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id || `${m.user_id}-${m.workspace_id}`} style={{ borderTop: "1px solid var(--pl-border)" }}>
                  <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace" }}>{m.user_id}</td>
                  <td style={tableStyles.td}><Pill>{m.role || "member"}</Pill></td>
                  <td style={tableStyles.td}>{(m.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

function SaaS({ workspace, plan }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
      <Card title="Subscription">
        <Row k="Plan ID" v={plan?.plan_id || "—"} />
        <Row k="Status" v={<StatusPill s={plan?.status || "active"} />} />
        <Row k="Billing cycle" v={plan?.billing_cycle || "monthly"} />
        <Row k="Stripe customer" v={plan?.stripe_customer_id || "—"} mono />
        <Row k="Stripe subscription" v={plan?.stripe_subscription_id || "—"} mono />
        <Row k="Trial ends" v={(plan?.trial_ends_at || "—").toString().slice(0, 19)} />
        <Row k="Current period ends" v={(plan?.current_period_end || "—").toString().slice(0, 19)} />
      </Card>
      <Card title="Lifecycle">
        <Row k="Created" v={(workspace.created_at || "").slice(0, 19)} />
        <Row k="Last activity" v={(workspace.updated_at || workspace.created_at || "").slice(0, 19)} />
        <Row k="Status" v={<StatusPill s={workspace.status} />} />
        <Row k="Workspace type" v={workspace.workspace_type} />
        <div style={{ marginTop: 12, padding: 10, borderRadius: 9, background: "rgba(252,211,77,0.10)", border: "1px solid rgba(252,211,77,0.32)", color: "#fcd34d", fontSize: 11 }}>
          Suspend / cancel / mark past-due actions will ship with the Stripe billing wiring (Turn D).
        </div>
      </Card>
    </div>
  );
}

function Credits({ workspaceId, balance, onChanged }) {
  const [txs, setTxs] = useState([]);
  const [amount, setAmount] = useState(100);
  const [reason, setReason] = useState("");
  const [op, setOp] = useState("add");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = () => api.get(`/api/admin/workspaces/${workspaceId}/credits`).then((d) => setTxs(d.transactions || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId]);

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) { setErr("Reason is required (min 3 chars)"); return; }
    setBusy(true); setErr("");
    try {
      await api.post(`/api/admin/workspaces/${workspaceId}/credits/${op}`, { amount: Number(amount), reason });
      setReason(""); await load(); onChanged && onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16 }}>
      <Card title="Balance">
        <div style={{ fontSize: 38, fontWeight: 900, color: "var(--pl-aqua)", lineHeight: 1 }}>{balance?.available_balance ?? 0}</div>
        <div style={{ fontSize: 11, color: "var(--pl-text-muted)", marginTop: 4 }}>Reserved: {balance?.reserved_balance ?? 0} · Monthly: {balance?.monthly_allowance ?? 0}</div>
        <div style={{ height: 1, background: "var(--pl-border)", margin: "16px 0" }} />
        <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800, marginBottom: 8 }}>Adjust credits</div>
        <Select value={op} onChange={setOp} options={[{ value: "add", label: "Grant (add)" }, { value: "debit", label: "Debit" }, { value: "adjust", label: "Adjust (signed)" }]}
          data-testid="credits-op" placeholder={null} />
        <div style={{ height: 8 }} />
        <TextInput type="number" value={amount} onChange={setAmount} placeholder="amount" data-testid="credits-amount" style={{ width: "100%" }} />
        <div style={{ height: 8 }} />
        <TextInput value={reason} onChange={setReason} placeholder="Reason (required)" data-testid="credits-reason" style={{ width: "100%" }} />
        {err && <div style={{ color: "#fda4af", fontSize: 11, marginTop: 8 }}>{err}</div>}
        <div style={{ height: 12 }} />
        <Btn testId="credits-submit" tone="primary" onClick={submit} disabled={busy} style={{ width: "100%", height: 38 }}>
          {busy ? "Saving…" : "Apply adjustment"}
        </Btn>
      </Card>
      <Card title="Ledger · most recent 100">
        <div style={{ maxHeight: 520, overflow: "auto" }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>When</th>
                <th style={tableStyles.th}>Type</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Amount</th>
                <th style={{ ...tableStyles.th, textAlign: "right" }}>Balance</th>
                <th style={tableStyles.th}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {txs.length === 0 ? (
                <tr><td colSpan={5} style={{ ...tableStyles.td, textAlign: "center", color: "var(--pl-text-muted)" }}>No transactions yet.</td></tr>
              ) : txs.map((t) => (
                <tr key={t.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                  <td style={{ ...tableStyles.td, fontSize: 11 }}>{(t.created_at || "").slice(0, 19)}</td>
                  <td style={tableStyles.td}><Pill tone={t.amount >= 0 ? "success" : "warn"}>{t.type}</Pill></td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace", color: t.amount >= 0 ? "var(--pl-aqua)" : "#fcd34d" }}>
                    {t.amount >= 0 ? `+${t.amount}` : t.amount}
                  </td>
                  <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{t.balance_after}</td>
                  <td style={tableStyles.td}>{t.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Features({ workspaceId }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/api/admin/workspaces/${workspaceId}/features`).then((d) => setItems(d.items));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId]);

  const toggle = async (key, enabled) => {
    try {
      await api.patch(`/api/admin/workspaces/${workspaceId}/features`, { feature_key: key, enabled });
      await load();
    } catch (e) { setErr(e.message); }
  };

  if (err) return <Err msg={err} />;
  if (!items) return <Loading />;

  return (
    <Card title="Features · workspace overrides">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
        {items.map((f) => (
          <div key={f.feature_key} data-testid={`feature-${f.feature_key}`} style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 12px", borderRadius: 10,
            background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)",
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 800 }}>{f.feature_key.replace(/_/g, " ")}</div>
              <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.14em", textTransform: "uppercase", marginTop: 3 }}>
                {f.source.replace(/_/g, " ")}
              </div>
            </div>
            <Toggle data-testid={`feature-toggle-${f.feature_key}`} checked={!!f.enabled} disabled={!!f.locked_by_plan}
              onChange={(v) => toggle(f.feature_key, v)} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function Limits({ workspaceId }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const load = () => api.get(`/api/admin/workspaces/${workspaceId}/limits`).then((d) => setItems(d.items));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspaceId]);

  const patch = async (limit_key, enabled, limit_value) => {
    try {
      await api.patch(`/api/admin/workspaces/${workspaceId}/limits`, {
        limit_key, enabled, limit_value: Number(limit_value || 0),
      });
      await load();
    } catch (e) { setErr(e.message); }
  };

  if (err) return <Err msg={err} />;
  if (!items) return <Loading />;

  return (
    <Card title="Usage limits">
      <table style={tableStyles.table}>
        <thead>
          <tr>
            <th style={tableStyles.th}>Limit</th>
            <th style={{ ...tableStyles.th, width: 90 }}>Enabled</th>
            <th style={{ ...tableStyles.th, width: 180 }}>Value</th>
            <th style={{ ...tableStyles.th, textAlign: "right", width: 120 }}>Current usage</th>
          </tr>
        </thead>
        <tbody>
          {items.map((l) => (
            <LimitRow key={l.limit_key} l={l} onApply={patch} />
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function LimitRow({ l, onApply }) {
  const [enabled, setEnabled] = useState(!!l.enabled);
  const [value, setValue] = useState(l.limit_value ?? 0);
  useEffect(() => { setEnabled(!!l.enabled); setValue(l.limit_value ?? 0); }, [l]);
  return (
    <tr style={{ borderTop: "1px solid var(--pl-border)" }} data-testid={`limit-${l.limit_key}`}>
      <td style={tableStyles.td}>
        <div style={{ fontWeight: 700, fontSize: 12.5 }}>{l.limit_key.replace(/_/g, " ")}</div>
        <div style={{ fontSize: 10, color: "var(--pl-text-muted)", marginTop: 2 }}>{l.source.replace(/_/g, " ")}</div>
      </td>
      <td style={tableStyles.td}>
        <Toggle checked={enabled} onChange={(v) => { setEnabled(v); onApply(l.limit_key, v, value); }}
          data-testid={`limit-toggle-${l.limit_key}`} />
      </td>
      <td style={tableStyles.td}>
        <input type="number" value={value} onChange={(e) => setValue(e.target.value)}
          onBlur={() => onApply(l.limit_key, enabled, value)}
          data-testid={`limit-value-${l.limit_key}`}
          style={{ height: 30, padding: "0 10px", borderRadius: 8, background: "var(--pl-bg-2)",
            border: "1px solid var(--pl-border)", color: "var(--pl-text)", fontSize: 12, width: 140, outline: "none" }} />
      </td>
      <td style={{ ...tableStyles.td, textAlign: "right", fontFamily: "JetBrains Mono, monospace" }}>{l.current_usage ?? 0}</td>
    </tr>
  );
}

function Usage({ workspaceId }) {
  const [u, setU] = useState(null);
  useEffect(() => { api.get(`/api/admin/workspaces/${workspaceId}/usage`).then(setU); }, [workspaceId]);
  if (!u) return <Loading />;
  const c = u.counts || {};
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
      {Object.entries(c).map(([k, v]) => (
        <Card key={k} pad={14}>
          <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>{k.replace(/_/g, " ")}</div>
          <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4 }}>{v}</div>
        </Card>
      ))}
    </div>
  );
}

function Audit({ workspaceId }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/api/admin/workspaces/${workspaceId}/audit`).then((d) => setItems(d.items)); }, [workspaceId]);
  if (!items) return <Loading />;
  return (
    <Card title={`Audit log · ${items.length}`}>
      {items.length === 0 ? <EmptyState title="No audit entries yet" /> : (
        <div style={{ maxHeight: 560, overflow: "auto" }}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>When</th>
                <th style={tableStyles.th}>Action</th>
                <th style={tableStyles.th}>Actor</th>
                <th style={tableStyles.th}>Resource</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} style={{ borderTop: "1px solid var(--pl-border)" }}>
                  <td style={tableStyles.td}>{(a.created_at || "").slice(0, 19)}</td>
                  <td style={{ ...tableStyles.td, color: "var(--pl-aqua)", fontWeight: 700 }}>{a.action}</td>
                  <td style={{ ...tableStyles.td, fontFamily: "JetBrains Mono, monospace" }}>{(a.actor_user_id || "").slice(0, 14)}</td>
                  <td style={tableStyles.td}>{a.resource_type} {a.resource_id ? `· ${(a.resource_id + "").slice(0, 14)}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ───────────────── small UI bits ───────────────── */

function Row({ k, v, mono }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 12, padding: "8px 0", borderBottom: "1px dashed var(--pl-border)", fontSize: 13 }}>
      <span style={{ color: "var(--pl-text-muted)", fontWeight: 700 }}>{k}</span>
      <span style={{ fontFamily: mono ? "JetBrains Mono, monospace" : undefined, color: "var(--pl-text)" }}>{v ?? "—"}</span>
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div style={{ padding: "10px 12px", borderRadius: 10, background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)" }}>
      <div style={{ fontSize: 10, color: "var(--pl-text-muted)", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 800 }}>{k}</div>
      <div style={{ marginTop: 4, fontWeight: 800, fontSize: 13 }}>{v}</div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, "data-testid": dt }) {
  return (
    <button type="button" data-testid={dt} onClick={() => !disabled && onChange(!checked)} disabled={disabled} style={{
      width: 36, height: 20, borderRadius: 999, border: 0, padding: 0, cursor: disabled ? "not-allowed" : "pointer",
      background: checked ? "linear-gradient(135deg, var(--pl-aqua), #B9FF2F)" : "var(--pl-bg-2)",
      position: "relative", transition: "background 160ms", opacity: disabled ? 0.5 : 1,
    }}>
      <span style={{
        position: "absolute", top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: 999,
        background: "#0a1816", transition: "left 160ms",
        boxShadow: "0 1px 4px rgba(0,0,0,0.6)",
      }} />
    </button>
  );
}
